import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import LoaderBox from "../HomePage/LoaderBox";

const FSHomeTurniri = () => {
  const [turniri, setTurniri] = useState([]);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  // --------------------------------------------------
  // SHARE
  // --------------------------------------------------

  const handleShare = async (e, turnir) => {
    e.stopPropagation();

    const shareUrl = `${window.location.origin}/${turnir.slug}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: turnir.naziv,
          text: `Provjeri turnir ${turnir.naziv} na FucaScore!`,
          url: shareUrl,
        });
      } catch (error) {
        /* console.log("Dijeljenje prekinuto:", error); */
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        alert("Link kopiran u međuspremnik!");
      } catch (error) {
        console.error("Greška pri kopiranju:", error);
      }
    }
  };

  // --------------------------------------------------
  // LOKALNA FUNKCIJA ZA PODRŠKU (SRCE)
  // --------------------------------------------------

  const handleSupport = async (e, turnirId) => {
    e.stopPropagation();

    const storageKey = `supported_turnir_${turnirId}`;
    const alreadySupported = localStorage.getItem(storageKey);

    if (alreadySupported) return;

    // Odmah spremimo u localStorage da spriječimo višestruki klik s istog uređaja
    localStorage.setItem(storageKey, "true");

    setTurniri((prevTurniri) =>
      prevTurniri.map((t) =>
        t.id === turnirId
          ? { ...t, support_count: (t.support_count || 0) + 1 }
          : t,
      ),
    );

    try {
      // Pozivamo sigurnu RPC funkciju umjesto direktnog update-a tablice
      const { error } = await supabase.rpc("increment_support", {
        row_id: turnirId,
      });

      if (error) throw error;
    } catch (error) {
      console.error("Greška pri spremanju podrške:", error);
    }
  };

  // --------------------------------------------------
  // FETCH TURNIRI
  // --------------------------------------------------

  useEffect(() => {
    const fetchAktivniTurniri = async () => {
      try {
        // ----------------------------------------------
        // TURNIRI
        // ----------------------------------------------

        const { data: turniriData, error: turniriError } = await supabase
          .from("admin_turniri")
          .select(
            `
              id,
              naziv,
              slug,
              logo_url,
              lokacija,
              naziv_lokacije,
              datum_od,
              datum_do,
              status,
              total_fund,
              broj_igraca,
              kotizacija,
              support_count
            `,
          )
          .eq("show_turnir", true)
          .order("datum_od", { ascending: true });

        if (turniriError) {
          throw turniriError;
        }

        // ----------------------------------------------
        // UTAKMICE
        // ----------------------------------------------

        const { data: utakmiceData, error: utakmiceError } = await supabase
          .from("turniri_utakmice")
          .select(
            `
              *,
              team_a:team_a_id(name),
              team_b:team_b_id(name)
            `,
          )
          .eq("is_completed", true);

        if (utakmiceError) {
          throw utakmiceError;
        }

        // ----------------------------------------------
        // TIMOVI
        // ----------------------------------------------

        const { data: timoviData, error: timoviError } = await supabase
          .from("turniri_timovi")
          .select("id, name, turnir_id, tournament_id");

        if (timoviError) {
          throw timoviError;
        }

        const timoviMap = new Map(
          (timoviData || []).map((team) => [team.id, team.name]),
        );

        const now = new Date();

        // ----------------------------------------------
        // OBRADA TURNIRA
        // ----------------------------------------------

        const obradjeniTurniri = (turniriData || [])
          .map((turnir) => {
            const tournamentId = turnir.id;

            // ------------------------------------------
            // BROJ EKIPA
            // ------------------------------------------

            const turnirTimovi = (timoviData || []).filter(
              (team) =>
                team.turnir_id === tournamentId ||
                team.tournament_id === tournamentId,
            );

            const brTimova = turnirTimovi.length;

            // ------------------------------------------
            // GRAMATIKA
            // ------------------------------------------

            let nazivEkipe = "EKIPA";

            if (brTimova === 1) {
              nazivEkipe = "EKIPA";
            } else if (
              brTimova % 10 >= 2 &&
              brTimova % 10 <= 4 &&
              (brTimova % 100 < 10 || brTimova % 100 >= 20)
            ) {
              nazivEkipe = "EKIPE";
            } else {
              nazivEkipe = "EKIPA";
            }

            // ------------------------------------------
            // DATUM ZAVRŠETKA
            // ------------------------------------------

            let isAfterEnd = false;

            if (turnir.datum_do) {
              const endDate = new Date(turnir.datum_do);

              endDate.setHours(23, 59, 59, 999);

              if (now > endDate) {
                isAfterEnd = true;
              }
            }

            // ------------------------------------------
            // UTAKMICE TURNIRA
            // ------------------------------------------

            const turnirUtakmice = (utakmiceData || []).filter(
              (match) =>
                match.turnir_id === tournamentId ||
                match.tournament_id === tournamentId,
            );

            // ------------------------------------------
            // POBJEDNIK
            // ------------------------------------------

            let dynamicWinner = null;

            if (turnirUtakmice.length > 0) {
              const finalMatch =
                turnirUtakmice.find((match) => match.bracket_slot_id === 15) ||
                turnirUtakmice.find((match) => {
                  const group = (match.group_name || "").toLowerCase();

                  return group.includes("finale");
                }) ||
                turnirUtakmice.reduce((max, match) => {
                  if (match.bracket_slot_id === 16) {
                    return max;
                  }

                  return (match.bracket_slot_id || 0) >
                    (max?.bracket_slot_id || 0)
                    ? match
                    : max;
                }, null);

              if (
                finalMatch &&
                (isAfterEnd || turnir.status === "zavrsen") &&
                finalMatch.is_completed
              ) {
                if (finalMatch.scorea > finalMatch.scoreb) {
                  dynamicWinner =
                    finalMatch.team_a?.name ||
                    timoviMap.get(finalMatch.team_a_id);
                } else if (finalMatch.scoreb > finalMatch.scorea) {
                  dynamicWinner =
                    finalMatch.team_b?.name ||
                    timoviMap.get(finalMatch.team_b_id);
                }
              }
            }

            // ------------------------------------------
            // JE LI ZAVRŠEN?
            // ------------------------------------------

            const isFinished = turnir.status === "zavrsen" || !!dynamicWinner;
            let isUpcoming = false;
            if (turnir.datum_od) {
              const startDate = new Date(turnir.datum_od);
              startDate.setHours(0, 0, 0, 0);
              if (now < startDate) {
                isUpcoming = true;
              }
            }
            return {
              ...turnir,
              isFinished,
              isUpcoming,
              brTimova,
              nazivEkipe,
              support_count: turnir.support_count || 0,
            };
          })
          .filter((turnir) => !turnir.isFinished);

        setTurniri(obradjeniTurniri);
      } catch (error) {
        console.error("Greška pri dohvatu aktivnih turnira:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAktivniTurniri();
  }, []);

  // --------------------------------------------------
  // DATE FORMAT
  // --------------------------------------------------

  const formatDateRange = (datumOd, datumDo) => {
    if (!datumOd) {
      return "";
    }

    const format = (dateString) => {
      const [year, month, day] = dateString.split("-");

      return `${day}.${month}.${year}.`;
    };

    if (!datumDo) {
      return format(datumOd);
    }

    return `${format(datumOd)} — ${format(datumDo)}`;
  };

  // --------------------------------------------------
  // LOADING
  // --------------------------------------------------

  if (loading) {
    return <LoaderBox text="Učitavanje turnira..." />;
  }

  // --------------------------------------------------
  // EMPTY
  // --------------------------------------------------

  if (turniri.length === 0) {
    return (
      <div className="scorify-empty">
        <div className="empty-icon">
          <i className="fa-solid fa-trophy" />
        </div>

        <h3>Nema aktivnih turnira</h3>

        <p>Trenutno nema otvorenih turnira u sustavu.</p>
      </div>
    );
  }

  // --------------------------------------------------
  // RENDER
  // --------------------------------------------------

  return (
    <>
      <section className="fshome-tournaments-section">
        {/* SECTION HEADER */}
        <div className="scorify-section-header">
          <div className="section-title-wrap">
            <span className="section-title-line" />

            <h3>Aktivni turniri</h3>
          </div>
        </div>

        {/* TOURNAMENTS */}
        <div className="tournaments-stack">
          {turniri.map((turnir) => {
            const isSupported = Boolean(
              localStorage.getItem(`supported_turnir_${turnir.id}`),
            );

            return (
              <article
                key={turnir.id}
                className="active-tournament-card"
                onClick={() => navigate(`/${turnir.slug}`)}
              >
                {/* AMBIENT GLOW */}
                <div className="active-card-glow" />

                {/* TOP */}
                <div className="active-card-top">
                  {/* LIVE */}
                  <div
                    className={`active-live-badge ${turnir.isUpcoming ? "upcoming-badge" : ""}`}
                  >
                    <span
                      className={`live-dot ${turnir.isUpcoming ? "upcoming-dot" : ""}`}
                    />
                    <span>{turnir.isUpcoming ? "USKORO" : "LIVE"}</span>
                  </div>

                  {/* ACTIONS (SUPPORT & SHARE) */}
                  <div className="active-card-actions">
                    <button
                      type="button"
                      className={`active-support-btn ${isSupported ? "supported" : ""}`}
                      onClick={(e) => handleSupport(e, turnir.id)}
                      title="Podrži turnir"
                    >
                      <i
                        className={
                          isSupported ? "fi fi-sr-heart" : "fi fi-br-heart"
                        }
                      />
                      <span className="support-badge-count">
                        {turnir.support_count}
                      </span>
                    </button>

                    <button
                      className="active-share-btn"
                      onClick={(e) => handleShare(e, turnir)}
                      title="Podijeli turnir"
                      type="button"
                    >
                      <i className="fi fi-rs-share" />
                    </button>
                  </div>
                </div>

                {/* MAIN */}
                <div className="active-card-main">
                  {/* LOGO */}
                  <div className="active-logo-container">
                    <img
                      src={turnir.logo_url || "default-logo-path.png"}
                      alt={turnir.naziv}
                    />
                  </div>

                  {/* INFO */}
                  <div className="active-tournament-info">
                    <h4>{turnir.naziv}</h4>
                    {/* DATE */}
                    <div className="active-date">
                      <i className="fa-regular fa-calendar-days" />

                      <span>
                        {formatDateRange(turnir.datum_od, turnir.datum_do)}
                      </span>
                    </div>
                    {turnir.naziv_lokacije && (
                      <div className="active-location">
                        <i className="fa-solid fa-location-dot" />

                        <span>{turnir.naziv_lokacije}</span>
                      </div>
                    )}

                    <div className="active-status-text">
                      <span className="status-mini-dot" />
                      Turnir je trenutno aktivan
                    </div>
                  </div>
                </div>

                {/* STATS */}
                <div className="active-tournament-stats">
                  {/* TEAMS */}
                  {turnir.brTimova > 0 && (
                    <div className="active-stat">
                      <span className="active-stat-value">
                        {turnir.brTimova}
                      </span>

                      <span className="active-stat-label">
                        {turnir.nazivEkipe}
                      </span>
                    </div>
                  )}

                  {/* PLAYERS */}
                  {turnir.broj_igraca && (
                    <div className="active-stat">
                      <span className="active-stat-value">
                        {turnir.broj_igraca - 1}
                        <small>+1</small>
                      </span>

                      <span className="active-stat-label">SASTAV</span>
                    </div>
                  )}

                  {/* ENTRY */}
                  {turnir.kotizacija && (
                    <div className="active-stat">
                      <span className="active-stat-value">
                        {turnir.kotizacija}€
                      </span>

                      <span className="active-stat-label">KOTIZACIJA</span>
                    </div>
                  )}

                  {/* FUND */}
                  {turnir.total_fund && Number(turnir.total_fund) > 0 && (
                    <div className="active-stat active-stat-fund">
                      <span className="active-stat-value">
                        {Number(turnir.total_fund).toLocaleString("de-DE")}€
                      </span>

                      <span className="active-stat-label">NAGRADNI FOND</span>
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </>
  );
};

export default FSHomeTurniri;
